import { faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { TDSSelfTaxPaymentsDetailsForm } from ".";
import { IFormITR1, ISelfTaxPayments } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const TDSSelfTaxPaymentsDetails: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<IFormITR1>();

  // const [showUploadChallanCanvas, setShowUploadChallanCanvas] = useState<boolean>(false);
  const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<ISelfTaxPayments | null>(null);
  const [activeItem, setActiveItem] = useState<ISelfTaxPayments | null>(null);
  // used in both edit and delete cases
  const [activeItemIdx, setActiveItemIdx] = useState<string>("");

  const SelfTaxPayments = cloneDeep(values.TaxesPaidTDSandTCS?.SelfTaxPayments || []);

  const handleDeleteItem = async () => {
    SelfTaxPayments.splice(Number(activeItemIdx), 1);
    setFieldValue("TaxesPaidTDSandTCS.SelfTaxPayments", SelfTaxPayments);
    setShowDeleteModal(() => null);
    setActiveItem(() => null);
    setActiveItemIdx(() => "");
    handleTotalCalculation();
  };

  const handleTotalCalculation = () => {
    const _SelfTaxPayments =
      SelfTaxPayments?.reduce((acc, curr) => {
        const value = Number(curr.Amount);
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    const currAssessYr = new Date().getFullYear();
    const prevAssessYr = currAssessYr - 1;

    // Months are 0-based in JavaScript
    const startDate = new Date(prevAssessYr, 3, 1); // 1 April 2023
    const endDate = new Date(currAssessYr, 2, 31); // 31 March 2024

    const june15 = new Date(2023, 5, 15);
    const sept15 = new Date(2023, 8, 15);
    const dec15 = new Date(2023, 11, 15);
    const march15 = new Date(2024, 2, 15);

    let TaxPaidBefore15June = 0;
    let TaxPaidBefore15Sept = 0;
    let TaxPaidBefore15Dec = 0;
    let TaxPaidBefore15Mar = 0;

    SelfTaxPayments?.forEach((element, index) => {
      const _DepositDate = new Date(element.DepositDate);

      if (_DepositDate > startDate && _DepositDate < endDate) {
        if (_DepositDate < june15) {
          TaxPaidBefore15June += Number(element.Amount);
        } else if (_DepositDate < sept15) {
          TaxPaidBefore15Sept += Number(element.Amount);
        } else if (_DepositDate < dec15) {
          TaxPaidBefore15Dec += Number(element.Amount);
        } else if (_DepositDate < march15) {
          TaxPaidBefore15Mar += Number(element.Amount);
        }
      }
    });

    setFieldValue("TaxPaidBefore15June", TaxPaidBefore15June);
    setFieldValue("TaxPaidBefore15Sept", TaxPaidBefore15Sept);
    setFieldValue("TaxPaidBefore15Dec", TaxPaidBefore15Dec);
    setFieldValue("TaxPaidBefore15Mar", TaxPaidBefore15Mar);
    setFieldValue("TotalSelfTaxPayments", _SelfTaxPayments);
    setFieldValue("TaxesPaidTDSandTCS.SelfTaxPayments", SelfTaxPayments);
  };

  return (
    <>
      <section className="mb-5">
        <div className="section-title-wrap">
          <h5>
            Self tax payments
            <small>
              Add entries here if you voluntarily deposited taxes to the government via Challan 280.
            </small>
          </h5>
          <div className="btns-wrap">
            {/* <Button
							variant="primaryDark"
							size="sm"
							onClick={() => setShowUploadChallanCanvas(() => true)}
						>
							<FASvgIcon icon={faUpload} />
							Upload Tax Paid Challan
						</Button> */}
            <PreventEditComponent>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddFormCanvas(() => true)}
              >
                <FASvgIcon icon={faPlus} />
                Add Manually
              </Button>
            </PreventEditComponent>
          </div>
        </div>
      </section>

      <section>
        <div className="section-title-wrap">
          <h5>
            Tax Paid Details - Summary Challan
            <small>
              Below is the summary of all the Challans added. Please review and proceed.
            </small>
          </h5>
        </div>

        <dl className="eds-list eds-dl eds-inline-between">
          {SelfTaxPayments?.map((tds, index) => (
            <div key={`sale-rent-deduction-${index}`} className="eds-list--item">
              <dt>{tds.ChallanNo}</dt>
              <dd>
                <span>
                  <PriceDisplay price={parseInt(tds?.Amount || "0")} />
                </span>
                <Button
                  variant="link"
                  onClick={() => {
                    setShowAddFormCanvas(() => true);
                    setActiveItem(() => tds);
                    setActiveItemIdx(() => String(index));
                  }}
                >
                  View
                  <PreventEditComponent>
                    <>/Edit</>
                  </PreventEditComponent>
                </Button>
                <Button
                  variant="transparent"
                  className="icon-only"
                  onClick={() => {
                    setShowDeleteModal(() => tds);
                    setActiveItemIdx(() => String(index));
                  }}
                >
                  <FASvgIcon icon={faTrash} />
                </Button>
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <TDSSelfTaxPaymentsDetailsForm
        showCanvas={showAddFormCanvas}
        handleCloseBtnPress={(tdsData) => {
          if (tdsData) {
            if (activeItem) {
              SelfTaxPayments?.splice(Number(activeItemIdx), 1, tdsData);
            } else {
              SelfTaxPayments?.push(tdsData);
            }
            handleTotalCalculation();
          }
          setShowAddFormCanvas(() => false);
          setActiveItem(() => null);
          setActiveItemIdx(() => "");
        }}
        formValues={activeItem}
      />

      <ConfirmationModal
        showModal={!!showDeleteModal}
        iconColorClass="text-danger"
        title="Are you sure?"
        message={<p>You want to delete {showDeleteModal?.ChallanNo}</p>}
        onYesBtnClick={() => handleDeleteItem()}
        onNoBtnClick={() => setShowDeleteModal(() => null)}
        icon={faTrashAlt}
      />

      {/* <TDSNonSalaryDetailsAddManuallyForm
				showCanvas={showUploadChallanCanvas}
				handleCloseBtnPress={(donationData) => {
					if (donationData) {
						if (activeItem) {
							NonSalaryTDS?.splice(Number(activeItemIdx), 1, donationData);
						} else {
							NonSalaryTDS?.push(donationData);
						}
						setFieldValue(
							"TaxesPaidTDSandTCS.NonSalaryTDS",
							NonSalaryTDS
						);
					}
					setShowUploadChallanCanvas(() => false);
					setActiveItem(() => null);
					setActiveItemIdx(() => "");
				}}
				formValues={activeItem}
			/> */}
    </>
  );
};

export default TDSSelfTaxPaymentsDetails;
